'use client';

import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import styles from './animated-background.module.scss';

const GEOMETRIC_SHAPES = [
  { size: 400, duration: 20, delay: 0, type: 'circle' as const },
  { size: 300, duration: 25, delay: 2, type: 'square' as const },
  { size: 350, duration: 22, delay: 4, type: 'triangle' as const },
  { size: 250, duration: 18, delay: 1, type: 'circle' as const },
  { size: 320, duration: 24, delay: 3, type: 'hexagon' as const },
];

const GRADIENT_ORBS = [
  { size: 500, x: 20, y: 30, color: 'purple' as const },
  { size: 600, x: 70, y: 60, color: 'blue' as const },
  { size: 450, x: 40, y: 80, color: 'purple' as const },
  { size: 550, x: 80, y: 20, color: 'blue' as const },
];

const PARTICLE_COUNT = 28;

function createParticles() {
  return Array.from({ length: PARTICLE_COUNT }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 3 + 1,
    duration: Math.random() * 10 + 20,
    delay: Math.random() * 5,
    color: Math.random() > 0.5 ? '#6F3DFF' : '#3D9FFF',
    opacity: Math.random() * 0.3 + 0.2,
  }));
}

export default function AnimatedBackground() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [particles, setParticles] = useState<ReturnType<typeof createParticles>>([]);
  const particlesRef = useRef<HTMLDivElement[]>([]);
  const mouseLightRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setParticles(createParticles());
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: e.clientX / window.innerWidth,
        y: e.clientY / window.innerHeight,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    if (particles.length === 0) return;
    let rafId: number;
    rafId = requestAnimationFrame(() => {
      const refs = particlesRef.current;
      particles.forEach((p, i) => {
        const el = refs[i];
        if (!el) return;
        const d = p.duration / 3;
        gsap.to(el, {
          keyframes: [
            { y: 0, x: 0, opacity: 0, scale: 0.5, duration: 0 },
            { y: -100, x: Math.sin(p.id) * 20, opacity: p.opacity * 2, scale: 1, duration: d },
            { y: 0, x: 0, opacity: 0, scale: 0.5, duration: d },
          ],
          repeat: -1,
          ease: 'sine.inOut',
          delay: p.delay,
        } as unknown as gsap.TweenVars);
      });
    });
    return () => {
      cancelAnimationFrame(rafId);
      particlesRef.current.forEach((el) => el && gsap.killTweensOf(el));
    };
  }, [particles]);

  useEffect(() => {
    const el = mouseLightRef.current;
    if (!el) return;
    const x = mousePosition.x * window.innerWidth - 300;
    const y = mousePosition.y * window.innerHeight - 300;
    gsap.to(el, { x, y, duration: 0.5, ease: 'power2.out', overwrite: true });
  }, [mousePosition.x, mousePosition.y]);

  return (
    <div className={styles.root}>
      <div className={styles.base} aria-hidden />

      {GRADIENT_ORBS.map((orb, index) => (
        <div
          key={`orb-${index}`}
          className={styles.orb}
          style={{
            width: orb.size,
            height: orb.size,
            left: `${orb.x}%`,
            top: `${orb.y}%`,
            background: `radial-gradient(circle, ${
              orb.color === 'purple' ? 'rgba(111, 61, 255, 0.2)' : 'rgba(61, 159, 255, 0.2)'
            } 0%, transparent 70%)`,
            ['--orb-fade-delay' as string]: `${index * 0.15}s`,
            ['--orb-float-dur' as string]: `${20 + index * 2}s`,
            ['--orb-float-delay' as string]: `${0.4 + index * 0.2}s`,
          }}
          aria-hidden
        />
      ))}

      {GEOMETRIC_SHAPES.map((shape, index) => (
        <div
          key={`shape-${index}`}
          className={styles.shape}
          style={{
            width: shape.size,
            height: shape.size,
            left: `${(index * 20) % 100}%`,
            top: `${(index * 25) % 100}%`,
            ['--shape-fade-delay' as string]: `${shape.delay * 0.2}s`,
            ['--shape-float-dur' as string]: `${shape.duration}s`,
            ['--shape-float-delay' as string]: `${0.3 + shape.delay * 0.2}s`,
          }}
          aria-hidden
        >
          {shape.type === 'circle' && <div className={styles.shapeCircle} />}
          {shape.type === 'square' && <div className={styles.shapeSquare} />}
          {shape.type === 'triangle' && <div className={styles.shapeTriangle} />}
          {shape.type === 'hexagon' && <div className={styles.shapeHexagon} />}
        </div>
      ))}

      <div className={styles.grid} aria-hidden />

      {particles.map((particle, index) => (
        <div
          key={`particle-${particle.id}`}
          ref={(r) => { if (r) particlesRef.current[index] = r; }}
          className={styles.particle}
          style={{
            width: particle.size,
            height: particle.size,
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            background: `radial-gradient(circle, ${particle.color} 0%, transparent 70%)`,
            boxShadow: `0 0 ${particle.size * 2}px ${particle.color}`,
          }}
          aria-hidden
        />
      ))}

      <div className={styles.radialOverlay} aria-hidden />
      <div className={styles.scanlines} aria-hidden />

      <div
        ref={mouseLightRef}
        className={styles.mouseLight}
        style={{ left: 0, top: 0 }}
        aria-hidden
      />

      <div className={styles.noise} aria-hidden />
      <div className={styles.vignette} aria-hidden />
    </div>
  );
}
